import type { ReactNode } from 'react'
import styles from './UserInfo.module.css'
import {
  Form,
  FormRow,
  FormColumns,
  FormColumn,
  FormActions,
  FormGroup,
  Help,
} from '@/components/Form'
import { Button } from '@/components/Button'
import { TextInput } from '@/components/inputs/TextInput'
import { NumericInput } from '@/components/inputs/NumericInput'
import { Checkbox } from '@/components/Checkbox'

export interface UserInfoProps {
  onSubmit: () => unknown
  givenNameInput: ReactNode
  familyNameInput: ReactNode
  emailInput: ReactNode
  phoneInput: ReactNode
  metadataFields: readonly { id: string; title: string; input: ReactNode }[]
  isSavePending: boolean
  canSave: boolean
}

export function UserInfo({
  onSubmit,
  givenNameInput,
  familyNameInput,
  emailInput,
  phoneInput,
  metadataFields,
  isSavePending,
  canSave,
}: UserInfoProps) {
  return (
    <div>
      <Form onSubmit={onSubmit}>
        <FormRow>
          <FormColumns>
            <FormColumn>{givenNameInput}</FormColumn>
            <FormColumn>{familyNameInput}</FormColumn>
          </FormColumns>
        </FormRow>
        <FormRow>
          <FormColumns>
            <FormColumn>{emailInput}</FormColumn>
            <FormColumn>{phoneInput}</FormColumn>
          </FormColumns>
        </FormRow>
        {metadataFields.map((field) => (
          <FormRow key={field.id}>
            <FormGroup label={field.title}>{field.input}</FormGroup>
          </FormRow>
        ))}
        <FormActions>
          <Button
            label="Save"
            type="submit"
            variant="primary"
            isPending={isSavePending}
            isDisabled={!canSave}
            title={
              canSave
                ? 'Save changes to your basic profile'
                : isSavePending
                  ? 'Saving...'
                  : 'No changes to save'
            }
          />
        </FormActions>
      </Form>
    </div>
  )
}

export interface MetadataInputFieldProps {
  type: 'TEXT' | 'NUMERIC' | 'BOOL'
  description?: string
  isDisabled: boolean
  value: unknown
  onChange: (value: string | number | boolean | null) => unknown
}

export function MetadataInputField({
  type,
  description,
  isDisabled,
  value,
  onChange,
}: MetadataInputFieldProps) {
  switch (type) {
    case 'NUMERIC':
      return (
        <>
          <NumericInput
            isDisabled={isDisabled}
            precision={2}
            min={0}
            step={1}
            value={value as number}
            onChange={(v) => onChange(v)}
          />
          <Help help={description} className={styles.spacing} />
        </>
      )
    case 'TEXT':
      return (
        <>
          <TextInput
            isDisabled={isDisabled}
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
          />
          <Help help={description} className={styles.spacing} />
        </>
      )
    case 'BOOL':
      return (
        <div className={styles.checkboxContainer}>
          <Checkbox
            isDisabled={isDisabled}
            checked={value as boolean}
            onChange={() => onChange(!value)}
          />
          <Help help={description} />
        </div>
      )
    default:
      return <></>
  }
}
